/**
 * AcceptOrgInvite — authenticated "join organization" step for an existing user.
 *
 * The org-invite email links to /register?token=…; for an email that already
 * has an account, RegisterWithInvite sends the user here. This page requires an
 * authenticated session and then calls /auth/accept-org-invitation, which
 * attaches the invited org + role + program assignments to the existing user.
 *
 * If the user isn't logged in yet, we bounce them through the REAL login page
 * (/login?next=…) so the full auth flow runs — password, 2FA (SMS/TOTP),
 * consent, device token — none of which the old inline login form on
 * RegisterWithInvite could handle. After login the page reloads here with the
 * token in localStorage and completes the accept.
 */

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { config } from '@/src/config';
import { AUTH_BACKDROP } from '@/src/platform/shared/components/authBackdrop';

type Status = 'working' | 'success' | 'error';

export default function AcceptOrgInvitePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [status, setStatus] = useState<Status>('working');
  const [message, setMessage] = useState<string>('Joining organization…');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('This link is missing its invitation token. Please use the link from your invitation email.');
      return;
    }

    const authToken = localStorage.getItem('auth_token');
    if (!authToken) {
      // Not signed in yet — send to the real login page and come back here so
      // the accept runs with a fully-authenticated session (handles 2FA/consent).
      const next = `/accept-org-invite?token=${encodeURIComponent(token)}`;
      navigate(`/login?next=${encodeURIComponent(next)}`, { replace: true });
      return;
    }

    const accept = async () => {
      try {
        const apiBase = (config.weaveinApi.baseUrl ?? '').replace(/\/$/, '');
        const res = await fetch(`${apiBase}/api/auth/accept-org-invitation`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify({ invitation_token: token }),
        });

        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          const detail = err.detail;
          throw new Error(
            (typeof detail === 'string' ? detail : detail?.message) || 'Failed to join organization.'
          );
        }

        setStatus('success');
        setMessage('You have joined the organization. Redirecting…');
        // Full reload so the app picks up the new org membership / session state.
        setTimeout(() => {
          window.location.href = '/';
        }, 1500);
      } catch (e: any) {
        setStatus('error');
        setMessage(e?.message || 'Failed to join organization. Please try again.');
      }
    };

    accept();
  }, [token, navigate]);

  return (
    <div className={`${AUTH_BACKDROP} flex items-center justify-center p-4`}>
      <div className="max-w-md w-full bg-white dark:bg-gray-900 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 p-8 text-center">
        {status === 'working' && (
          <>
            <Loader2 className="w-10 h-10 mx-auto text-v2accent animate-spin" />
            <p className="mt-4 text-gray-700 dark:text-gray-200">{message}</p>
          </>
        )}
        {status === 'success' && (
          <>
            <CheckCircle className="w-12 h-12 mx-auto text-green-500" />
            <h1 className="mt-4 text-xl font-semibold text-gray-900 dark:text-gray-100">Organization joined</h1>
            <p className="mt-2 text-gray-600 dark:text-gray-300">{message}</p>
          </>
        )}
        {status === 'error' && (
          <>
            <AlertCircle className="w-12 h-12 mx-auto text-red-500" />
            <h1 className="mt-4 text-xl font-semibold text-gray-900 dark:text-gray-100">Couldn’t join the organization</h1>
            <p className="mt-2 text-gray-600 dark:text-gray-300">{message}</p>
            <button
              onClick={() => navigate('/login')}
              className="mt-6 inline-flex items-center justify-center rounded-lg bg-v2accent px-4 py-2 text-white hover:brightness-90"
            >
              Go to sign in
            </button>
          </>
        )}
      </div>
    </div>
  );
}
